import '@/styles/globals.css';
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import initSentry from '@/utils/sentry';

// Initialize Sentry
if (import.meta.env.PROD) {
  initSentry();
}
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { getAppText, useLanguageStore } from '@/stores/languageStore';
import { authService } from '@/firebase/auth';

// Components
import { Sidebar } from '@/components/common/Sidebar';
import { Header } from '@/components/common/Header';
import { MobileTabBar } from '@/components/common/MobileTabBar';
import { AquaLionRun } from '@/components/common/AquaLionRun';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import UpdateNotification from '@/components/common/UpdateNotification';
import { getAppVersionInfo } from '@/utils/version';
import { consumeAquaLionRunFlag } from '@/utils/loginCelebration';

const lazyNamed = (factory, exportName) => lazy(() =>
  factory().then((module) => ({ default: module[exportName] }))
);

// Pages loaded on demand to reduce initial bundle size.
const AuthPage = lazyNamed(() => import('@/pages/AuthPage'), 'AuthPage');
const Dashboard = lazyNamed(() => import('@/pages/Dashboard'), 'Dashboard');
const ActivitiesPage = lazyNamed(() => import('@/pages/ActivitiesPage'), 'ActivitiesPage');
const ExercisesPage = lazyNamed(() => import('@/pages/ExercisesPage'), 'ExercisesPage');
const WorkoutsPage = lazyNamed(() => import('@/pages/WorkoutsPage'), 'WorkoutsPage');
const CalendarPage = lazyNamed(() => import('@/pages/CalendarPage'), 'CalendarPage');
const TimerPage = lazyNamed(() => import('@/pages/TimerPage'), 'TimerPage');
const StatsPage = lazyNamed(() => import('@/pages/StatsPage'), 'StatsPage');
const ChallengesPage = lazyNamed(() => import('@/pages/ChallengesPage'), 'ChallengesPage');
const GroupsPage = lazyNamed(() => import('@/pages/GroupsPage'), 'GroupsPage');
const PlayerPanelPage = lazyNamed(() => import('@/pages/PlayerPanelPage'), 'PlayerPanelPage');
const AdminPage = lazyNamed(() => import('@/pages/AdminPage'), 'AdminPage');
const CoachDashboardPage = lazyNamed(() => import('@/pages/CoachDashboardPage'), 'CoachDashboardPage');
const HelpPage = lazyNamed(() => import('@/pages/HelpPage'), 'HelpPage');
const PrivacyTermsPage = lazyNamed(() => import('@/pages/PrivacyTermsPage'), 'PrivacyTermsPage');
const NotFoundPage = lazyNamed(() => import('@/pages/NotFoundPage'), 'NotFoundPage');

const routerFutureFlags = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

const RouteFallback = ({ text = getAppText('pl') }) => (
  <div className="min-h-[40vh] flex items-center justify-center px-4">
    <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white/90 p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800/90">
      <div className="flex items-center gap-3">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary-600 border-t-transparent" aria-hidden="true" />
        <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{text.loadingView}</p>
      </div>
      <p className="mt-2 text-xs text-neutral-600 dark:text-neutral-300">
        {text.loadingDesc}
      </p>
    </div>
  </div>
);

const ProtectedLayout = ({ children, onLogout, appVersionInfo, text }) => {
  const location = useLocation();
  const showDashboardHeader = location.pathname === '/';

  return (
    <div className="app-scaffold">
      <div className="app-shell">
        <Sidebar onLogout={onLogout} />
        <div className="app-content-wrap">
          {showDashboardHeader ? (
            <Header title={text.userPanel} subtitle={text.userPanelSubtitle} showUserPanel />
          ) : null}
          <main className="app-main">
            <div className="app-page-container">
              {children}
            </div>
          </main>
          <footer className="app-footer text-[11px] text-neutral-500 dark:text-neutral-400">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>{text.appVersion}: {appVersionInfo.label}</span>
              <span>{text.build}: {appVersionInfo.buildId || 'local'}</span>
            </div>
          </footer>
        </div>
      </div>
      <MobileTabBar />
    </div>
  );
};

const AppRoutes = () => {
  const location = useLocation();

  return (
    <div key={location.pathname} className="route-genie-frame">
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/activities" element={<ActivitiesPage />} />
        <Route path="/exercises" element={<ExercisesPage />} />
        <Route path="/workouts" element={<WorkoutsPage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/training" element={<TimerPage />} />
        <Route path="/timer" element={<Navigate to="/training" replace />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/challenges" element={<ChallengesPage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/player" element={<PlayerPanelPage />} />
        <Route path="/coach" element={<CoachDashboardPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/privacy-terms" element={<PrivacyTermsPage />} />
        <Route path="/ai-stats" element={<Navigate to="/coach" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
};

function App() {
  const { user, setUser, setLoading } = useAuthStore();
  const { isDarkMode, appTheme, initTheme } = useThemeStore();
  const { appLanguage, initLanguage } = useLanguageStore();
  const appVersionInfo = getAppVersionInfo();
  const [showAquaLionRun, setShowAquaLionRun] = useState(false);
  const t = getAppText(appLanguage);

  useEffect(() => {
    initTheme();
    initLanguage();
  }, [initTheme, initLanguage]);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    document.documentElement.setAttribute('data-app-theme', appTheme || 'aqua');
  }, [appTheme]);

  useEffect(() => {
    if (!user || appTheme !== 'aqua') return;
    if (consumeAquaLionRunFlag()) {
      setShowAquaLionRun(true);
    }
  }, [user, appTheme]);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [setUser, setLoading]);

  const handleLogout = async () => {
    await authService.logout();
    setUser(null);
  };

  const handleAquaLionRunComplete = useCallback(() => {
    setShowAquaLionRun(false);
  }, []);

  if (!user) {
    return (
      <Router future={routerFutureFlags}>
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback text={t} />}>
            <AuthPage />
          </Suspense>
        </ErrorBoundary>
      </Router>
    );
  }

  return (
    <Router future={routerFutureFlags}>
      <ProtectedLayout onLogout={handleLogout} appVersionInfo={appVersionInfo} text={t}>
        <ErrorBoundary>
          <Suspense fallback={<RouteFallback text={t} />}>
            <AppRoutes />
          </Suspense>
        </ErrorBoundary>
      </ProtectedLayout>
      {appTheme === 'aqua' && showAquaLionRun ? <AquaLionRun onComplete={handleAquaLionRunComplete} /> : null}
      <UpdateNotification />
      <Toaster />
    </Router>
  );
}

export default App;
